import { css } from '@emotion/react';
import { useMemo } from 'react';

import {
  getGroupWorkStatusSummary,
  getTotalCostIfDefined,
  groupHasScopeChanges,
  isScanPartScoped,
  ScanGroupOptimized,
  WorkStatusSummary,
} from '@kaiizen/kai-scan-template-system';

import { WarningDelta } from '@/Client/Icons';
import { useConfirmationModal } from '@/Client/State/ConfirmationModalContext';
import { useNavigateToScan } from '@/Client/State/kaiNavigation';
import { AddPhotoIcon } from '@/Client/Widgets/StyledIcons';
import { ScanDataMutators, useScanEditingContextSlice } from '@kaiizen/kai-pro-api-react-state';
import { formatUsd, groupNamePresenter } from '../../../Presenters';
import { COLORS2, LAYOUT2, TYPOGRAPHY2 } from '../../../Styles';
import { TaskIcons } from '../ProgressWalk/WorkStatusIcons';
import { CloneIcon, DeleteIcon } from './ScanNavigatorIcons';
import { ScanNavigatorItem } from './ScanNavigatorItem';

const GROUP_ITEM_EDITING_STYLE = css`
  label: GROUP_ITEM_EDITING_STYLE;
  color: ${COLORS2.alwaysDark.text.white};
`;

const GROUP_TITLE_STYLE = css`
  label: GROUP_TITLE_STYLE;
  flex: 1;
  align-items: center;
  overflow: hidden;
  display: flex;
  gap: ${LAYOUT2.spacingP5}px;
  min-width: 0;
`;

const GROUP_NAME_TEXT_STYLE = css`
  label: GROUP_NAME_TEXT_STYLE;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: ${COLORS2.alwaysDark.text.white};
`;

const GROUP_PHOTO_COUNT_STYLE = css`
  label: GROUP_PHOTO_COUNT_STYLE;
  color: ${COLORS2.alwaysDark.text.white};
`;

const CLONE_BUTTON_STYLE = css`
  label: CLONE_BUTTON_STYLE;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
  margin-left: ${LAYOUT2.spacing1}px;

  &:hover {
    opacity: 0.7;
  }
`;

export const ScanNavigatorGroupItem = ({
  group,
  baselineGroup,
  isCurrentGroup,
  isEditing,
  canDeleteGroup,
  scanDataMutators,
  progress,
}: {
  group: ScanGroupOptimized;
  baselineGroup?: ScanGroupOptimized;
  isCurrentGroup?: boolean;
  isEditing?: boolean;
  canDeleteGroup?: boolean;
  scanDataMutators?: ScanDataMutators;
  progress?: boolean;
}) => {
  const { scanId, defaultContractorsByTrade, baselineDefaultContractorsByTrade } = useScanEditingContextSlice([
    'scanId',
    'defaultContractorsByTrade',
    'baselineDefaultContractorsByTrade',
  ]);
  const navigateToScan = useNavigateToScan();
  const confirm = useConfirmationModal();
  const totalCost = getTotalCostIfDefined(group);
  const textColor = COLORS2.alwaysDark.text.white;

  const workStatusSummary: WorkStatusSummary | undefined = useMemo(() => progress ? getGroupWorkStatusSummary(group) : undefined, [group, progress]);

  const cloneGroup = async (group: ScanGroupOptimized) => scanDataMutators?.cloneGroup(group.id);

  const deleteGroup = (group: ScanGroupOptimized) => {
    const scopedLineItemCount = group.tasks?.flatMap(task =>
      task.options?.flatMap(option =>
        option.lineItems?.filter(isScanPartScoped)
          ?? []
      ) ?? []
    )?.length ?? 0;

    confirm({
      title: 'Delete Group',
      message: `Delete this group? There are ${scopedLineItemCount} scoped line items in this group.`,
      onConfirm: () => scanDataMutators?.deleteGroup(group.id),
    });
  };

  const hasChanges = useMemo(() => {
    return group && baselineGroup && groupHasScopeChanges(group, baselineGroup, defaultContractorsByTrade, baselineDefaultContractorsByTrade);
  }, [group, baselineGroup, defaultContractorsByTrade, baselineDefaultContractorsByTrade]);

  return (
    <ScanNavigatorItem
      isSelected={isCurrentGroup}
      onSelect={() => navigateToScan({ scanId, groupId: group.id, progress })}
    >
      <div css={[GROUP_TITLE_STYLE, isEditing && GROUP_ITEM_EDITING_STYLE]}>
        {hasChanges && <WarningDelta size={TYPOGRAPHY2.fontSize.xSmall} color={COLORS2.alwaysDark.red} />}
        {workStatusSummary && <TaskIcons workStatus={workStatusSummary.workStatus} />}
        <div css={GROUP_NAME_TEXT_STYLE}>{groupNamePresenter(group)}</div>
        {!progress && group.optimized.allPhotoIds.length > 0 && (
          <>
            <AddPhotoIcon color={textColor} width={10} height={10} />
            <div css={GROUP_PHOTO_COUNT_STYLE}>
              {group.optimized.allPhotoIds.length}
            </div>
          </>
        )}
      </div>
      {workStatusSummary
        ? `${workStatusSummary.completedCount} / ${workStatusSummary.count}`
        : totalCost && (
          <div>
            {formatUsd(totalCost, true)}
          </div>
        )}
      {isEditing && (
        <>
          {canDeleteGroup && (
            <button
              css={CLONE_BUTTON_STYLE}
              onClick={e => {
                e.stopPropagation();
                deleteGroup(group);
              }}
              aria-label='Delete group'
            >
              <DeleteIcon />
            </button>
          )}
          <button
            css={CLONE_BUTTON_STYLE}
            onClick={e => {
              e.stopPropagation();
              cloneGroup(group);
            }}
            aria-label='Clone group'
          >
            <CloneIcon />
          </button>
        </>
      )}
    </ScanNavigatorItem>
  );
};
